import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';

const DAY_FOCUS = {};

export default function SessionDetail() {
  const { id } = useParams();
  const queryClient = useQueryClient();

  const { data: session, isLoading } = useQuery({
    queryKey: ['session', parseInt(id)],
    queryFn: () => api.getSession(id),
  });

  const [notesDraft, setNotesDraft] = useState('');
  const [notesReady, setNotesReady] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);

  useEffect(() => {
    if (session && !notesReady) {
      setNotesDraft(session.notes || '');
      setNotesReady(true);
    }
  }, [session, notesReady]);

  const updateNotesMutation = useMutation({
    mutationFn: () => api.updateSession(session.id, { notes: notesDraft || null }),
    onSuccess: () => {
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 2000);
      queryClient.invalidateQueries({ queryKey: ['session', parseInt(id)] });
    },
  });

  const deleteSetMutation = useMutation({
    mutationFn: (setId) => api.deleteSet(id, setId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['session', parseInt(id)] }),
  });

  if (isLoading) return <div className="loading"><div className="spinner" />Chargement...</div>;
  if (!session) return <div className="alert alert-error">Séance introuvable</div>;

  // Group sets by exercise, en gardant l'ordre d'arrivée (les clés numériques d'un objet JS
  // seraient sinon réordonnées automatiquement par valeur croissante, ignorant le tri SQL)
  const byExercise = {};
  const exerciseOrder = [];
  for (const set of session.sets || []) {
    if (!byExercise[set.exercise_id]) {
      byExercise[set.exercise_id] = { name: set.exercise_name, sets: [], isAlternative: !!set.alternative_to };
      exerciseOrder.push(set.exercise_id);
    }
    byExercise[set.exercise_id].sets.push(set);
  }
  // Masque les exercices sans aucune donnée réelle (ex: enregistrement vide par erreur)
  const orderedExercises = exerciseOrder
    .filter((exId) => byExercise[exId].sets.some((s) => s.weight != null || s.reps != null || s.notes))
    .map((exId) => [exId, byExercise[exId]]);

  const totalVolume = (session.sets || []).reduce(
    (acc, s) => acc + (s.reps || 0) * (s.weight || 0),
    0
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <Link to="/history" className="btn btn-ghost btn-sm">← Retour</Link>
      </div>

      <h1 className="page-title">{DAY_FOCUS[session.day] || session.day}</h1>
      <p className="page-subtitle">
        {new Date(session.date).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
      </p>

      {/* Notes de séance */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header" style={{ marginBottom: 10 }}>
          <span className="card-title">📝 Notes de séance</span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <textarea
            className="form-input"
            style={{ flex: 1, minHeight: 68, resize: 'vertical', fontFamily: 'inherit' }}
            placeholder="Ajouter une note (état physique, conditions, PR…)"
            value={notesDraft}
            onChange={(e) => { setNotesDraft(e.target.value); setNotesSaved(false); }}
          />
          <button
            className={`btn ${notesSaved ? 'btn-success' : 'btn-primary'} btn-sm`}
            onClick={() => updateNotesMutation.mutate()}
            disabled={updateNotesMutation.isPending}
            style={{ flexShrink: 0 }}
          >
            {notesSaved ? '✓' : 'Sauvegarder'}
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-3" style={{ marginBottom: 28 }}>
        <div className="card">
          <div className="stat-value">{orderedExercises.reduce((acc, [, ex]) => acc + ex.sets.length, 0)}</div>
          <div className="stat-label">Séries effectuées</div>
        </div>
        <div className="card">
          <div className="stat-value">{Math.round(totalVolume).toLocaleString('fr-FR')}</div>
          <div className="stat-label">Volume total (kg)</div>
        </div>        <div className="card">
          <div className="stat-value">{orderedExercises.length}</div>
          <div className="stat-label">Exercices</div>
        </div>
      </div>

      {/* Exercises */}
      {orderedExercises.map(([exId, ex]) => {
        const exVolume = ex.sets.reduce((acc, s) => acc + (s.reps || 0) * (s.weight || 0), 0);
        return (
          <div key={exId} className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <div>
                <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {ex.name}
                  {ex.isAlternative && (
                    <span style={{ fontSize: 11, background: 'var(--warning)', color: '#000', borderRadius: 4, padding: '1px 6px', fontWeight: 700, flexShrink: 0 }}>ALT</span>
                  )}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 2 }}>
                  Volume: <strong style={{ color: 'var(--accent2)' }}>{Math.round(exVolume).toLocaleString('fr-FR')} kg</strong>
                </div>
              </div>
              <Link to={`/stats?exercise=${exId}`} className="btn btn-ghost btn-sm">
                📈 Progression
              </Link>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Série</th>
                    <th>Poids (kg)</th>
                    <th>Répétitions</th>
                    <th>Note</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {ex.sets.map((s) => (
                    <tr key={s.id}>
                      <td><span className="set-num" style={{ display: 'inline-flex' }}>{s.set_number}</span></td>
                      <td><strong>{s.weight ?? '—'}</strong></td>
                      <td><strong>{s.reps ?? '—'}</strong></td>
                      <td style={{ color: 'var(--text2)', fontSize: 13 }}>{s.notes || '—'}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ color: 'var(--danger)', padding: '2px 8px', fontSize: 15, lineHeight: 1 }}
                          title="Supprimer cette série"
                          onClick={() => window.confirm(`Supprimer la série ${s.set_number} ?`) && deleteSetMutation.mutate(s.id)}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {Object.keys(byExercise).length === 0 && (
        <div className="empty-state">
          <div className="icon">🤷</div>
          <p>Aucune série enregistrée pour cette séance</p>
        </div>
      )}
    </div>
  );
}
